// Abschnitt als gruppierte Karte (Handoff §4, iOS-Einstellungen): Icon-Kachel + Titel + rechts „N Pflicht“.
// Ohne Icon wird der Kopf zur kleinen Überzeile (Prototyp „Hochgeladene Dateien“).
import type { ReactNode } from 'react';
import type { Icon } from '@phosphor-icons/react';
import { abschnittZaehler } from './form-logik';
import { ABSCHNITT, ABSCHNITT_FARBE, FORM, type AbschnittFarbe } from './form-stil';

export interface FormAbschnittProps {
  titel: string;
  icon?: Icon;
  farbe?: AbschnittFarbe;
  /** Anzahl Pflichtfelder im Abschnitt. */
  pflicht?: number;
  /** Anzahl rot markierter Felder im Abschnitt. */
  fehlt?: number;
  /** Eigener Inhalt rechts im Kopf (statt Pflicht-Zähler). */
  rechts?: ReactNode;
  tip?: string;
  /** Position im Formular – verzögert das Einblenden leicht (Prototyp: 0,18 s + 0,06 s je Abschnitt). */
  index?: number;
  children: ReactNode;
}

const NEUTRAL_TOENUNG = 'rgba(255,255,255,0.05)';

export function FormAbschnitt({ titel, icon: I, farbe, pflicht = 0, fehlt = 0, rechts, tip, index = 0, children }: FormAbschnittProps) {
  const f = farbe ? ABSCHNITT_FARBE[farbe] : null;
  const zaehler = abschnittZaehler(pflicht, fehlt);
  return (
    // Kein overflow:hidden an der Karte – sonst würden Aufklapplisten (Empfänger, Farbmenüs) abgeschnitten.
    // Zugeschnitten wird nur das Wasserzeichen in seiner eigenen Ebene.
    <section aria-label={titel} className="pf-rise relative flex shrink-0 flex-col" style={{
      gap: I ? 12 : 8, padding: 12, borderRadius: ABSCHNITT.radius,
      background: `linear-gradient(165deg,${f?.toenung ?? NEUTRAL_TOENUNG},rgba(255,255,255,0.035) 55%,rgba(255,255,255,0.02))`,
      boxShadow: ABSCHNITT.schatten, animationDelay: `${(0.18 + index * 0.06).toFixed(2)}s`,
    }}>
      {I && f && (
        <span aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden" style={{ borderRadius: ABSCHNITT.radius }}>
          <I size={130} weight="fill" className="absolute"
            style={{ right: -22, bottom: -30, color: f.akzent, opacity: 0.07, transform: 'rotate(-14deg)' }} />
        </span>
      )}
      <div data-tip={tip} className="relative flex items-center" style={{ gap: 10, padding: I ? '2px 2px 0' : '2px 4px' }}>
        {I && f && (
          <span aria-hidden className="relative shrink-0 overflow-hidden" style={{
            width: 32, height: 32, borderRadius: 10, background: f.verlauf,
            boxShadow: `inset 0 1px 1px rgba(255,255,255,0.5),inset 0 -2px 4px rgba(0,0,0,0.2),0 6px 12px -6px ${f.akzent}`,
          }}>
            <span className="absolute inset-0" style={{ background: 'linear-gradient(180deg,rgba(255,255,255,0.34),rgba(255,255,255,0) 50%)' }} />
            <span className="absolute rounded-full" style={{ width: 22, height: 22, right: -7, bottom: -8, background: 'rgba(255,255,255,0.16)' }} />
            <span className="absolute inset-0 flex items-center justify-center">
              <I size={16} weight="fill" color="#fff" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }} />
            </span>
          </span>
        )}
        {I ? (
          <h3 className="min-w-0 flex-1 truncate" style={{ margin: 0, fontSize: 14.5, fontWeight: 800, letterSpacing: '-0.01em', color: FORM.text }}>{titel}</h3>
        ) : (
          <h3 className="min-w-0 flex-1 truncate" style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: FORM.text3 }}>{titel}</h3>
        )}
        {rechts ?? (zaehler && (
          <span style={{
            fontSize: 11, fontWeight: 800, padding: '4px 9px', borderRadius: 999,
            color: zaehler.fehlt ? FORM.rot : ABSCHNITT.zaehler,
            background: zaehler.fehlt ? ABSCHNITT.fehltFlaeche : ABSCHNITT.zaehlerFlaeche,
          }}>{zaehler.text}</span>
        ))}
      </div>
      <div className="relative flex flex-col" style={{ gap: 8 }}>{children}</div>
    </section>
  );
}
